<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { ChevronLeft, ChevronRight, ChevronsUpDown, ExternalLink, Plus } from '@lucide/vue'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { navigation } from '@/app/navigation'
import { can } from '@/lib/permissions/can'
import { Permission } from '@/lib/permissions/keys'
import CreateWorkspaceDialog from '@/features/workspaces/components/CreateWorkspaceDialog.vue'
import { createSsoTarget, useSsoRedirect } from '@/features/sso/mutations/useSsoRedirect'
import { useAuthStore } from '@/stores/auth'
import { useSubscribedProducts } from '@/features/billing/composables/useSubscribedProducts'
import { useWorkspaceStore } from '@/stores/workspace'

const { t } = useI18n()
const route = useRoute()
const auth = useAuthStore()
const workspace = useWorkspaceStore()
const { state, toggleSidebar } = useSidebar()
const { mutate: openInNewTab } = useSsoRedirect()
const subscribedProducts = useSubscribedProducts()

const createWorkspaceOpen = ref(false)

// Workspaces are created inside the selected organization, so that's the scope that must allow it.
const canCreateWorkspace = computed(
  () =>
    workspace.currentOrganizationId !== null &&
    can(Permission.WorkspaceCreate, { organizationId: workspace.currentOrganizationId }),
)

const visibleItems = computed(() =>
  navigation.filter(
    (item) =>
      (!item.systemAdminOnly || auth.user?.isSystemAdmin === true) &&
      (!item.permission || can(item.permission)),
  ),
)

function isActive(path?: string): boolean {
  if (!path) return false
  return path === '/dashboard' ? route.path === path : route.path.startsWith(path)
}

function handleNewTabClick(): void {
  // Opened synchronously so the browser doesn't treat it as a popup; the SSO URL is loaded once fetched.
  const tab = window.open('', '_blank')
  if (tab) tab.opener = null
  openInNewTab(createSsoTarget(tab))
}
</script>

<template>
  <Sidebar collapsible="icon" class="top-(--header-height) h-[calc(100svh-var(--header-height))]">
    <SidebarHeader class="border-b p-2">
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <button
            type="button"
            :aria-label="t('navigation.switchWorkspace')"
            class="hover:bg-sidebar-accent flex w-full items-center justify-between gap-2 rounded-md px-4 py-3.5 text-left transition-colors group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0"
          >
            <span
              class="bg-primary/10 text-primary hidden size-8 items-center justify-center rounded-md text-xs font-semibold group-data-[collapsible=icon]:flex"
            >
              {{ workspace.currentAccount?.name.charAt(0).toUpperCase() }}
            </span>
            <span class="min-w-0 leading-tight group-data-[collapsible=icon]:hidden">
              <span class="text-muted-foreground block text-[11px]">{{
                t('navigation.workspace')
              }}</span>
              <span class="mt-0.5 block truncate text-sm font-medium">{{
                workspace.currentAccount?.name ?? t('navigation.noWorkspace')
              }}</span>
              <span class="text-muted-foreground block truncate text-[10px]">
                {{
                  subscribedProducts
                    ?.map((product) => t(`billing.plans.${product}.name`))
                    .join(', ')
                }}
              </span>
            </span>
            <ChevronsUpDown
              class="text-muted-foreground size-4 shrink-0 group-data-[collapsible=icon]:hidden"
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="w-56" align="start" side="bottom">
          <DropdownMenuLabel>{{ t('navigation.switchWorkspace') }}</DropdownMenuLabel>
          <DropdownMenuItem
            v-for="account in workspace.accountsInCurrentOrganization"
            :key="account.id"
            @select="workspace.setCurrentAccount(account.id)"
          >
            {{ account.name }}
          </DropdownMenuItem>
          <template v-if="canCreateWorkspace">
            <DropdownMenuSeparator />
            <DropdownMenuItem @select="createWorkspaceOpen = true">
              <Plus class="size-4" />
              {{ t('navigation.addWorkspace') }}
            </DropdownMenuItem>
          </template>
        </DropdownMenuContent>
      </DropdownMenu>
      <CreateWorkspaceDialog v-model:open="createWorkspaceOpen" />
    </SidebarHeader>

    <SidebarContent>
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            <template v-for="item in visibleItems" :key="item.titleKey">
              <li v-if="item.separatorBefore" role="presentation" class="my-2">
                <SidebarSeparator class="mx-0" />
              </li>
              <SidebarMenuItem>
                <SidebarMenuButton
                  as-child
                  :is-active="isActive(item.route)"
                  :tooltip="t(item.titleKey)"
                  class="h-9 px-3"
                >
                  <button v-if="item.opensInNewTab" type="button" @click="handleNewTabClick">
                    <component :is="item.icon" />
                    <span class="group-data-[collapsible=icon]:hidden">{{ t(item.titleKey) }}</span>
                    <ExternalLink
                      class="text-muted-foreground ml-auto size-3.5 group-data-[collapsible=icon]:hidden"
                      aria-hidden="true"
                    />
                  </button>
                  <RouterLink v-else :to="item.route!">
                    <component :is="item.icon" />
                    <span class="group-data-[collapsible=icon]:hidden">{{ t(item.titleKey) }}</span>
                  </RouterLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </template>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </SidebarContent>

    <button
      type="button"
      :aria-label="t('navigation.toggleSidebar')"
      class="bg-background hover:bg-muted absolute top-[30px] -right-3.5 group-data-[collapsible=icon]:top-[10px] z-20 flex size-7 items-center justify-center rounded-full border shadow-sm transition-colors"
      @click="toggleSidebar"
    >
      <ChevronLeft v-if="state === 'expanded'" class="size-4" />
      <ChevronRight v-else class="size-4" />
    </button>
  </Sidebar>
</template>
